import { useEffect, useMemo, useReducer, useState, useSyncExternalStore } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ApiError, feedbackConversationAuthorityFingerprint, getFeedbackPublication,
  isCurrentResourceCommandScope } from '@/lib/api';
import { captureCoachingManualCommand } from '@/lib/coachingManualCommands';
import { feedbackPublicationKey, invalidateScope, queryClient } from '@/lib/queryClient';
import { useAuthStore } from '@/stores/auth';
import { useScopeStore } from '@/stores/scope';
import type { CreateGoalFromFeedbackRequest, FeedbackPublicationResponse, ResourceReadScope } from '@/lib/types';

export type GoalFeedbackEntry = { entryId: string; feedbackId: string; scope: Readonly<ResourceReadScope> };
type GoalCommand = { action: 'create-goal-from-feedback'; body: CreateGoalFromFeedbackRequest };
type Operation = ReturnType<typeof captureCoachingManualCommand<GoalCommand>>;
const abort = () => new DOMException('Goal source context changed', 'AbortError');
const eligible = (value: FeedbackPublicationResponse) => value.subject_mode === 'players'
  && !!value.goal_source_authoring?.player_ids.length;

/** One mounted form owns its captured source and original in-memory operation. */
export function useGoalFromFeedback(entry?: GoalFeedbackEntry) {
  const [, changed] = useReducer(value => value + 1, 0);
  const [life] = useState(() => ({ entry: entry ? { ...entry, scope: { ...entry.scope } } : undefined,
    authority: feedbackConversationAuthorityFingerprint(), departed: false, mounted: true, busy: false,
    source: null as FeedbackPublicationResponse | null, operation: null as Operation | null,
    problem: '', denied: false }));
  const boundary = useMemo(() => {
    const current = () => {
      if (!life.entry || !isCurrentResourceCommandScope(life.entry.scope)
        || life.authority !== feedbackConversationAuthorityFingerprint()) life.departed = true;
      return !!life.entry && !life.departed;
    };
    return { current, subscribe: (notify: () => void) => {
      const observe = () => { current(); notify(); };
      const authOff = useAuthStore.subscribe(observe), scopeOff = useScopeStore.subscribe(observe);
      observe(); return () => { authOff(); scopeOff(); };
    } };
  }, [life]);
  const active = useSyncExternalStore(boundary.subscribe, boundary.current, () => false)
    && entry?.entryId === life.entry?.entryId && entry?.feedbackId === life.entry?.feedbackId
    && JSON.stringify(entry?.scope) === JSON.stringify(life.entry?.scope);
  const query = useQuery({
    queryKey: life.entry ? feedbackPublicationKey(life.entry.scope, life.entry.feedbackId) : ['goal-source-inactive'],
    queryFn: ({ signal }) => getFeedbackPublication(life.entry!.feedbackId, { scope: life.entry!.scope, signal }),
    enabled: active, retry: false, staleTime: 0, refetchOnWindowFocus: false,
  }, queryClient);
  useEffect(() => {
    if (active && query.isFetchedAfterMount && !query.isFetching && !query.error && query.data && !life.source) {
      life.source = query.data; changed();
    }
  }, [active, query.isFetchedAfterMount, query.isFetching, query.error, query.data, life]);
  useEffect(() => { life.mounted = true; return () => { life.mounted = false; life.operation?.dispose(); }; }, [life]);
  const check = () => { if (!active || !life.mounted || !boundary.current()) throw abort(); };
  const fail = (error: unknown) => {
    if (error instanceof ApiError && [401, 403, 404].includes(error.status)) life.denied = true;
    life.problem = error instanceof Error && error.name === 'AbortError' ? 'Coaching context changed. Your writing is kept.'
      : 'The goal is not confirmed. Your writing and original operation are kept.';
  };
  async function execute() {
    check();
    const result = await life.operation!.send(); check();
    // Refresh is independent of the acknowledged Goal. Never create again.
    void invalidateScope().catch(() => {});
    return result;
  }
  async function create(goal: CreateGoalFromFeedbackRequest['goal']) {
    check(); if (life.busy || life.operation) return null;
    life.busy = true; life.problem = ''; changed();
    try {
      const origin = life.source;
      if (!origin || !eligible(origin)) throw new Error('Goal source unavailable');
      const latest = await getFeedbackPublication(life.entry!.feedbackId, { scope: life.entry!.scope }); check();
      if (!eligible(latest) || latest.feedback_revision_id !== origin.feedback_revision_id
        || latest.feedback_version !== origin.feedback_version
        || !latest.goal_source_authoring!.player_ids.includes(goal.player_id)) {
        life.problem = 'Feedback changed or is unavailable. Reload the source to review it; your writing is kept.';
        return null;
      }
      life.operation = captureCoachingManualCommand(life.entry!.scope, { action: 'create-goal-from-feedback', body: {
        schema_version: 'goal-from-feedback.v1', operation_id: crypto.randomUUID(),
        feedback_id: origin.feedback_id, feedback_revision_id: origin.feedback_revision_id,
        expected_feedback_version: origin.feedback_version, goal,
      } });
      return await execute();
    } catch (error) { fail(error); return null; }
    finally { life.busy = false; changed(); }
  }
  async function retry() {
    check(); if (life.busy || !life.operation) return null;
    life.busy = true; life.problem = ''; changed();
    try { return await execute(); }
    catch (error) { fail(error); return null; }
    finally { life.busy = false; changed(); }
  }
  async function reload() {
    check(); if (life.busy || life.operation) return;
    life.busy = true; changed();
    try {
      const value = await query.refetch(); check();
      if (value.error || !value.data) throw value.error;
      life.source = value.data; life.denied = false; life.problem = ''; changed();
    } catch (error) { fail(error); }
    finally { life.busy = false; changed(); }
  }
  const state = life.operation?.getState();
  const source = active && !query.error && !life.denied ? life.source : null;
  return { active, source, busy: life.busy, state,
    problem: life.problem || (query.error ? 'Feedback could not be loaded. Your writing is kept.' : ''),
    loading: active && !query.isFetchedAfterMount,
    canCreate: active && !!source && eligible(source) && !life.operation && !life.busy && !life.problem,
    locked: !!life.operation || life.busy,
    create, retry, reload,
    editRejected() {
      check(); if (life.operation?.getState().status !== 'rejected' || life.busy) return;
      life.operation.dispose(); life.operation = null; life.problem = ''; changed();
    },
  };
}
